// src/odoo/helpdesk_dashboard/hooks/useHelpdeskDashboard.ts
// All comments are in English.

import { useQuery } from "@tanstack/react-query";
import {
  fetchHelpdeskAssigneesMonthly,
  fetchHelpdeskAssigneesOverall,
  fetchHelpdeskIssueTypes,
  fetchHelpdeskMonthlyTrend,
  fetchHelpdeskMonthlyTrendTickets,
  fetchHelpdeskOpenAging,
  fetchHelpdeskStageDistribution,
  fetchHelpdeskSubTypes,
  fetchHelpdeskSummary,
  fetchHelpdeskSalespersonsOverall,
  fetchHelpdeskTickets,
  fetchHelpdeskTopCustomers,
  fetchHelpdeskTopProducts,
} from "../api/helpdeskDashboardApi";
import type {
  AssigneeMonthlyPoint,
  CustomerIssueTypePoint,
  HelpdeskDashboardFilters,
  HelpdeskTicketDrilldownFilters,
  HelpdeskSummaryResponse,
  MonthlyTrendPoint,
  MonthlyTrendTicketInfo,
  NamedCountPoint,
  OpenAgingPoint,
} from "../api/helpdeskDashboardApi";

export function useHelpdeskSummary(filters: HelpdeskDashboardFilters) {
  return useQuery<HelpdeskSummaryResponse, Error>({
    queryKey: ["helpdesk-dashboard", "summary", filters],
    queryFn: () => fetchHelpdeskSummary(filters),
    keepPreviousData: true,
  });
}

export function useHelpdeskMonthlyTrend(filters: HelpdeskDashboardFilters) {
  return useQuery<MonthlyTrendPoint[], Error>({
    queryKey: ["helpdesk-dashboard", "monthly-trend", filters],
    queryFn: () => fetchHelpdeskMonthlyTrend(filters),
    keepPreviousData: true,
  });
}

export function useHelpdeskMonthlyTrendTickets(
  filters: HelpdeskDashboardFilters,
  month: string | undefined,
  enabled = true
) {
  return useQuery<MonthlyTrendTicketInfo[], Error>({
    queryKey: ["helpdesk-dashboard", "monthly-trend", "tickets", filters, month],
    queryFn: () => fetchHelpdeskMonthlyTrendTickets(filters, month ?? ""),
    enabled: enabled && Boolean(month),
    keepPreviousData: true,
  });
}

export function useHelpdeskTickets(
  filters: HelpdeskTicketDrilldownFilters,
  enabled = true
) {
  return useQuery<MonthlyTrendTicketInfo[], Error>({
    queryKey: ["helpdesk-dashboard", "tickets", filters],
    queryFn: () => fetchHelpdeskTickets(filters),
    enabled,
    keepPreviousData: true,
  });
}

export function useHelpdeskStageDistribution(filters: HelpdeskDashboardFilters) {
  return useQuery<NamedCountPoint[], Error>({
    queryKey: ["helpdesk-dashboard", "stage-distribution", filters],
    queryFn: () => fetchHelpdeskStageDistribution(filters),
    keepPreviousData: true,
  });
}

export function useHelpdeskIssueTypes(filters: HelpdeskDashboardFilters) {
  return useQuery<NamedCountPoint[], Error>({
    queryKey: ["helpdesk-dashboard", "issue-types", filters],
    queryFn: () => fetchHelpdeskIssueTypes(filters),
    keepPreviousData: true,
  });
}

export function useHelpdeskSubTypes(filters: HelpdeskDashboardFilters) {
  return useQuery<NamedCountPoint[], Error>({
    queryKey: ["helpdesk-dashboard", "sub-types", filters],
    queryFn: () => fetchHelpdeskSubTypes(filters),
    keepPreviousData: true,
  });
}

export function useHelpdeskAssigneesMonthly(filters: HelpdeskDashboardFilters) {
  return useQuery<AssigneeMonthlyPoint[], Error>({
    queryKey: ["helpdesk-dashboard", "assignees-monthly", filters],
    queryFn: () => fetchHelpdeskAssigneesMonthly(filters),
    keepPreviousData: true,
  });
}

export function useHelpdeskAssigneesOverall(filters: HelpdeskDashboardFilters) {
  return useQuery<NamedCountPoint[], Error>({
    queryKey: ["helpdesk-dashboard", "assignees-overall", filters],
    queryFn: () => fetchHelpdeskAssigneesOverall(filters),
    keepPreviousData: true,
  });
}

export function useHelpdeskSalespersonsOverall(filters: HelpdeskDashboardFilters) {
  return useQuery<NamedCountPoint[], Error>({
    queryKey: ["helpdesk-dashboard", "salespersons-overall", filters],
    queryFn: () => fetchHelpdeskSalespersonsOverall(filters),
    keepPreviousData: true,
  });
}

export function useHelpdeskTopProducts(filters: HelpdeskDashboardFilters) {
  return useQuery<NamedCountPoint[], Error>({
    queryKey: ["helpdesk-dashboard", "top-products", filters],
    queryFn: () => fetchHelpdeskTopProducts(filters),
    keepPreviousData: true,
  });
}

export function useHelpdeskTopCustomers(filters: HelpdeskDashboardFilters) {
  return useQuery<CustomerIssueTypePoint[], Error>({
    queryKey: ["helpdesk-dashboard", "top-customers", filters],
    queryFn: () => fetchHelpdeskTopCustomers(filters),
    keepPreviousData: true,
  });
}

export function useHelpdeskOpenAging(filters: HelpdeskDashboardFilters) {
  return useQuery<OpenAgingPoint[], Error>({
    queryKey: ["helpdesk-dashboard", "open-aging", filters],
    queryFn: () => fetchHelpdeskOpenAging(filters),
    keepPreviousData: true,
  });
}
